import { Button, Field, Input, Text, makeStyles, tokens } from '@fluentui/react-components';
import { formatTimeOfDay, parseTimeOfDay } from '@worklogix/shared';
import { useEffect, useState } from 'react';

/**
 * A date and a time you can TYPE.
 *
 * Michael, 2026-09-17: "I also don't like the way the times are edited too. It's a
 * really frustrating UI that uses scrolling to pick dates and times. Can you make that
 * easier to use and less frustrating?"
 *
 * The native `datetime-local` control makes you spin a wheel to reach a value you
 * already know — and on a timecard you do know it, because you are reading it off a
 * punch or a paper sheet. So the time is a plain text box that accepts what a person
 * would write (`7:05a`, `705`, `0705`, `7:05 AM`, `19:05`) and normalises on blur;
 * see `parseTimeOfDay`, which is where the midnight/noon edge cases are pinned down.
 *
 * The ±15 buttons are for the correction that actually happens: a clock-out that was a
 * bit later than the punch says. Nudging beats retyping a whole timestamp, and it
 * CARRIES ACROSS MIDNIGHT — +15 on 23:55 moves to the next day rather than wrapping
 * back to the start of the same one, which would silently move the shift a day early.
 *
 * VALUE CONTRACT unchanged: `YYYY-MM-DDTHH:mm`, exactly what `datetime-local` emitted,
 * so nothing downstream had to change.
 */

const useStyles = makeStyles({
  row: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'flex-end', flexWrap: 'wrap' },
  date: { minWidth: '150px' },
  time: { width: '110px' },
  nudge: { display: 'flex', gap: '4px' },
  invalid: { color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200 },
});

export interface DateTimeFieldProps {
  label: string;
  /** `YYYY-MM-DDTHH:mm`, or '' when unset (an open shift has no clock-out). */
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  /**
   * Date to assume when this field is EMPTY, as `YYYY-MM-DD`.
   *
   * 🔥 An open shift has no clock-out, so the field starts blank — and a blank date
   * box is how somebody ends up entering a time against the wrong day. A clock-out
   * almost always belongs to the day the shift STARTED, so that is what it defaults
   * to; anyone closing a night shift can still change it. Sandbox, 2026-09-17: an
   * edit landed on 06:00 the FOLLOWING morning, which is exactly this shape of
   * mistake.
   */
  defaultDate?: string;
}

/** Split the field value; '' yields empty parts rather than throwing. */
function splitValue(value: string): { date: string; time: string } {
  const [date = '', time = ''] = value.split('T');
  return { date, time: time.slice(0, 5) };
}

export function DateTimeField({
  label,
  value,
  onChange,
  disabled = false,
  defaultDate,
}: DateTimeFieldProps): JSX.Element {
  const styles = useStyles();
  const split = splitValue(value);
  // An empty field shows the shift's own day rather than nothing at all.
  const date = split.date === '' ? (defaultDate ?? '') : split.date;
  const { time } = split;
  // Held separately so a half-typed time ("7:") is not destroyed on every keystroke;
  // it is normalised on blur, or reported as invalid if it never parses.
  const [timeText, setTimeText] = useState(time);
  const [touched, setTouched] = useState(false);

  // Follow the value when the parent swaps sessions — without this, opening a second
  // entry would show the previous one's time.
  useEffect(() => {
    setTimeText(splitValue(value).time);
    setTouched(false);
  }, [value]);

  const parsed = parseTimeOfDay(timeText);
  const invalid = touched && timeText.trim() !== '' && parsed === null;

  const commitTime = (): void => {
    if (parsed === null) {
      return;
    }
    const normalised = formatTimeOfDay(parsed);
    setTimeText(normalised);
    // `date` already carries the fallback, so this can never emit a dateless value
    // like "T06:00" — which the API rejects and which used to be reachable by typing
    // a time into an open shift's clock-out.
    if (date === '') {
      return;
    }
    onChange(`${date}T${normalised}`);
  };

  /** Shift by whole minutes, carrying the DATE across midnight in either direction. */
  const nudge = (deltaMinutes: number): void => {
    if (date === '' || parsed === null) {
      return;
    }
    // Anchored at UTC noon so the arithmetic never crosses a DST boundary and lands
    // an hour out; only the date and the minute count are read back.
    const at = new Date(`${date}T12:00:00Z`);
    const total = parsed + deltaMinutes;
    at.setUTCDate(at.getUTCDate() + Math.floor(total / 1440));
    const minutes = ((total % 1440) + 1440) % 1440;
    onChange(`${at.toISOString().slice(0, 10)}T${formatTimeOfDay(minutes)}`);
  };

  return (
    <Field label={label}>
      <div className={styles.row}>
        <Input
          className={styles.date}
          type="date"
          value={date}
          disabled={disabled}
          onChange={(_, d) => {
            onChange(`${d.value}T${parsed === null ? time : formatTimeOfDay(parsed)}`);
          }}
        />
        <Input
          className={styles.time}
          value={timeText}
          disabled={disabled}
          placeholder="7:05a"
          aria-label={`${label} time`}
          onChange={(_, d) => {
            setTimeText(d.value);
            setTouched(true);
          }}
          onBlur={commitTime}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              commitTime();
            }
          }}
        />
        <div className={styles.nudge}>
          <Button size="small" disabled={disabled || parsed === null} onClick={() => { nudge(-15); }}>
            −15m
          </Button>
          <Button size="small" disabled={disabled || parsed === null} onClick={() => { nudge(15); }}>
            +15m
          </Button>
        </div>
      </div>
      {invalid ? (
        <Text className={styles.invalid} role="alert">
          Not a time — try 7:05a, 705, or 19:05.
        </Text>
      ) : null}
    </Field>
  );
}
